import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";

/**
 * Phase 3I.4N-B-2 + 3I.4O.1 — canonical bundle-aware Add Subscription modal.
 *
 * PRODUCT
 *   - orangecare
 *   - optimise_pro
 *   - combined (OrangeCare + Orange Optimise Pro)
 *
 * Every price is fetched from the backend:
 *   POST /api/hq/control/subscriptions/quote
 *
 * The modal NEVER computes prices, FX, rounding, or add-on maths.
 *
 * Phase 3I.4O.1 semantics for custom CRM:
 *   - ONE cadence-specific CRM input (never both monthly + annual).
 *   - The entered amount is IN THE SELECTED CURRENCY. The backend
 *     interprets it as target-currency-native and applies zero FX.
 *   - Cadence or currency change clears the CRM input + invalidates
 *     the latest quote to prevent stale reuse across denominations.
 */

const CURRENCIES = ["GBP", "USD", "EUR", "AED"];
const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const PRODUCTS = [
  { key: "orangecare",   label: "OrangeCare" },
  { key: "optimise_pro", label: "Orange Optimise Pro" },
  { key: "combined",     label: "OrangeCare + Orange Optimise Pro" },
];
const SERVICES = [
  { key: "landing_page",         label: "Landing Page" },
  { key: "premium_website",      label: "Full / Custom Website" },
  { key: "crm_business_system",  label: "Custom CRM & Business Systems" },
];
const CADENCES = [
  { key: "monthly", label: "Monthly" },
  { key: "annual",  label: "Annual"  },
];

const money = (minor, cur) =>
  `${CUR_SYMBOL[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const majorToMinor = (v) => {
  const f = parseFloat(v);
  if (!Number.isFinite(f) || f < 0) return null;
  return Math.round(f * 100);
};

/** Canonical form key drives stale-quote protection. */
function canonicalFormKey(form) {
  const {
    product, service, cadence, currency, crm_custom_amount_minor,
  } = form;
  return JSON.stringify({
    product,
    service: (product === "optimise_pro") ? null : service,
    cadence, currency,
    crm_custom_amount_minor: (service === "crm_business_system") ? crm_custom_amount_minor : null,
  });
}

export default function AddSubscriptionModal({
  open, onClose, businessId, defaultContact, defaultEmail, defaultName,
  onDraftCreated,
}) {
  const [form, setForm] = useState({
    product: "combined",
    service: "landing_page",
    cadence: "monthly",
    currency: "GBP",
    crm_custom_amount_minor: null,
  });
  const [crmInput, setCrmInput] = useState("");
  const [toEmail, setToEmail]   = useState(defaultEmail || "");
  const [toName]                = useState(defaultName  || "");
  const [quote, setQuote]       = useState(null);
  const [quoteKey, setQuoteKey] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoting, setQuoting]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setToEmail(defaultEmail || "");
  }, [open, defaultEmail]);

  // Sync CRM input → form.
  useEffect(() => {
    setForm((f) => ({ ...f, crm_custom_amount_minor: majorToMinor(crmInput) }));
  }, [crmInput]);

  // For solo Optimise Pro the service is not user-selectable.
  useEffect(() => {
    if (form.product === "optimise_pro" && form.service) {
      setForm((f) => ({ ...f, service: null }));
    } else if (form.product !== "optimise_pro" && !form.service) {
      setForm((f) => ({ ...f, service: "landing_page" }));
    }
  }, [form.product]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cadence/currency-change safety (Phase 3I.4O.1 §"CADENCE CHANGE SAFETY" +
  // §"CURRENCY CHANGE SAFETY"): clear the CRM input + invalidate the quote.
  // Only relevant when the operator is on a CRM path.
  const onCadenceChange = (newCadence) => {
    setForm((f) => ({ ...f, cadence: newCadence, crm_custom_amount_minor: null }));
    if (form.service === "crm_business_system") setCrmInput("");
    setQuote(null); setQuoteKey(null); setQuoteError(null);
  };
  const onCurrencyChange = (newCurrency) => {
    setForm((f) => ({ ...f, currency: newCurrency, crm_custom_amount_minor: null }));
    if (form.service === "crm_business_system") setCrmInput("");
    setQuote(null); setQuoteKey(null); setQuoteError(null);
  };
  const onServiceChange = (newService) => {
    setForm((f) => ({ ...f, service: newService, crm_custom_amount_minor: null }));
    setCrmInput("");
    setQuote(null); setQuoteKey(null); setQuoteError(null);
  };
  const onProductChange = (newProduct) => {
    setForm((f) => ({ ...f, product: newProduct, crm_custom_amount_minor: null }));
    setCrmInput("");
    setQuote(null); setQuoteKey(null); setQuoteError(null);
  };

  const currentKey = useMemo(() => canonicalFormKey(form), [form]);
  const quoteStale = quoteKey !== currentKey;

  // ---------- Quote fetch (debounced) --------------------------------------
  const fetchQuote = useCallback(async (snapshotForm) => {
    setQuoting(true); setQuoteError(null);
    try {
      const payload = {
        product:  snapshotForm.product,
        cadence:  snapshotForm.cadence,
        currency: snapshotForm.currency,
      };
      if (snapshotForm.product !== "optimise_pro") payload.service_type = snapshotForm.service;
      if (snapshotForm.service === "crm_business_system") {
        payload.crm_custom_amount_minor = snapshotForm.crm_custom_amount_minor;
      }
      const r = await hqApi.post("/control/subscriptions/quote", payload);
      setQuote(r.data);
      setQuoteKey(canonicalFormKey(snapshotForm));
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = (typeof d === "object" && d?.code) || (typeof d === "string" ? d : "quote_failed");
      setQuoteError(code); setQuote(null); setQuoteKey(null);
    } finally {
      setQuoting(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const snapshot = { ...form };
      const needsCrm = snapshot.service === "crm_business_system"
        && (snapshot.product === "orangecare" || snapshot.product === "combined");
      if (needsCrm && !snapshot.crm_custom_amount_minor) {
        setQuote(null); setQuoteKey(null); setQuoteError(null); return;
      }
      fetchQuote(snapshot);
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, currentKey, fetchQuote, form]);

  // ---------- Create bundle --------------------------------------
  const canCreate = !!quote && !quoteStale && !quoting && !saving && !!toEmail;

  const create = async () => {
    if (!canCreate) return;
    setSaving(true);
    try {
      const payload = {
        product:  form.product,
        cadence:  form.cadence,
        currency: form.currency,
        contact_id: defaultContact?.id,
      };
      if (form.product !== "optimise_pro") payload.service_type = form.service;
      if (form.service === "crm_business_system") {
        payload.crm_custom_amount_minor = form.crm_custom_amount_minor;
      }
      const r = await hqApi.post(
        `/control/clients/${businessId}/subscriptions/bundle`, payload,
      );
      toast.success("Subscription created");
      onDraftCreated?.(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.code || d?.message || "Could not create subscription");
    } finally { setSaving(false); }
  };

  if (!open) return null;

  const currency = form.currency;
  const showService = form.product !== "optimise_pro";
  const showCrmInput = form.service === "crm_business_system" && showService;
  const crmCadenceLabel = form.cadence === "annual" ? "Annual" : "Monthly";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="hq-add-sub-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">New Subscription</div>
            <h2 className="font-black text-lg mt-1">Add subscription</h2>
          </div>
          <button data-testid="hq-add-sub-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]">
            <X size={16}/>
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto text-sm">
          {/* Client + Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Client">
              <div className="px-3 py-2 rounded-lg bg-[#F6F5F2] font-bold">{toName || "—"}</div>
            </Field>
            <Field label="Email">
              <input
                data-testid="hq-add-sub-email"
                type="email" value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"
              />
            </Field>
          </div>

          {/* Product */}
          <Field label="Product">
            <select
              data-testid="hq-add-sub-product"
              value={form.product}
              onChange={(e) => onProductChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border"
            >
              {PRODUCTS.map((p) => (
                <option key={p.key} value={p.key} data-testid={`hq-add-sub-product-opt-${p.key}`}>{p.label}</option>
              ))}
            </select>
          </Field>

          {/* Service (hidden for solo Optimise Pro) */}
          {showService && (
            <Field label="Service">
              <select
                data-testid="hq-add-sub-service"
                value={form.service || ""}
                onChange={(e) => onServiceChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"
              >
                {SERVICES.map((s) => (
                  <option key={s.key} value={s.key} data-testid={`hq-add-sub-service-opt-${s.key}`}>{s.label}</option>
                ))}
              </select>
            </Field>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Billing frequency">
              <select
                data-testid="hq-add-sub-cadence"
                value={form.cadence}
                onChange={(e) => onCadenceChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"
              >
                {CADENCES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Currency">
              <select
                data-testid="hq-add-sub-currency"
                value={form.currency}
                onChange={(e) => onCurrencyChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Custom CRM — ONE cadence-specific input in the selected currency */}
          {showCrmInput && (
            <div className="rounded-xl border border-[#FCE1C7] bg-[#FFF7EE] p-4"
                 data-testid="hq-add-sub-crm-panel">
              <Field label={`Custom CRM — ${crmCadenceLabel} OrangeCare price (${currency})`}>
                <div className="flex items-center">
                  <span className="px-3 py-2 rounded-l-lg border border-r-0 bg-[#F6F5F2] text-[11px] font-black tracking-[0.14em]">
                    {CUR_SYMBOL[currency] || currency}
                  </span>
                  <input
                    data-testid="hq-add-sub-crm-amount"
                    type="number" step="0.01" min="0"
                    value={crmInput}
                    onChange={(e) => setCrmInput(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-r-lg border font-black"
                    placeholder={form.cadence === "annual" ? "e.g. 2100.00" : "e.g. 175.00"}
                  />
                </div>
              </Field>
              <p className="mt-2 text-[11px] text-[#8A6D00]">
                Enter the amount in <span className="font-black">{currency}</span> for the <span className="font-black">{crmCadenceLabel.toLowerCase()}</span> cadence.
                No currency conversion is applied to the custom CRM figure.
              </p>
            </div>
          )}

          {/* Canonical live quote */}
          <div className="rounded-xl border border-[#EAEAEA] p-4" data-testid="hq-add-sub-quote">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00] flex items-center gap-2">
              Live quote{quoting && <Loader2 size={12} className="animate-spin"/>}
            </div>
            {quoteError && (
              <div className="mt-2 p-2 rounded-lg bg-[#FFECEC] text-[#C1350B] text-[12px] font-bold" data-testid="hq-add-sub-quote-error">
                {quoteError}
              </div>
            )}
            {quote && !quoteError && (
              <div className="mt-2 space-y-1 text-sm" data-testid="hq-add-sub-quote-lines">
                {quote.lines.map((l) => (
                  <div key={l.service_key} className="flex justify-between" data-testid={`hq-add-sub-quote-line-${l.service_key}`}>
                    <span>{l.label}</span>
                    <span className="font-bold tabular-nums">{money(l.amount_minor, currency)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-[#EAEAEA] text-base">
                  <span className="font-black">Total</span>
                  <span className="font-black tabular-nums" data-testid="hq-add-sub-quote-total">
                    {money(quote.total_minor, currency)} / {form.cadence === "annual" ? "year" : "month"}
                  </span>
                </div>
              </div>
            )}
            {!quote && !quoteError && !quoting && (
              <div className="mt-2 text-[11px] text-[#8A8A8A]" data-testid="hq-add-sub-quote-idle">
                {showCrmInput
                  ? `Enter the custom CRM ${crmCadenceLabel.toLowerCase()} price in ${currency} to see the live total.`
                  : "Fetching canonical price…"}
              </div>
            )}
            {quote && quoteStale && (
              <div className="mt-2 text-[11px] text-[#8A6D00] font-bold" data-testid="hq-add-sub-quote-stale">
                Waiting for fresh quote…
              </div>
            )}
          </div>

          <p className="text-[11px] text-[#8A8A8A]">
            Every price is fetched from the canonical backend pricing engine. Routing: AED → Stripe · GBP/USD/EUR → PayPal.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-[#EAEAEA] flex items-center justify-end gap-2">
          <button data-testid="hq-add-sub-cancel" onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">
            Cancel
          </button>
          <button
            data-testid="hq-add-sub-create"
            onClick={create}
            disabled={!canCreate}
            className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${canCreate ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"}`}
          >
            {saving ? <Loader2 size={12} className="inline animate-spin mr-1"/> : null}
            Create subscription
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      {children}
    </div>
  );
}
